'use client';

import React from 'react';
import {
  PersonIcon,
  FileTextIcon,
  UpdateIcon,
  CrossCircledIcon,
  InfoCircledIcon,
  ChatBubbleIcon,
  GearIcon,
  LockClosedIcon,
} from '@radix-ui/react-icons';

import {
  Box,
  BoxProps,
  Text,
  Heading,
  Badge,
  Button,
  Spinner,
  EmptyState,
  Avatar,
} from '../../index';
import { formatDistanceToNow } from '../../utils/date';

export interface Activity {
  id: string;
  type:
    | 'create'
    | 'update'
    | 'delete'
    | 'comment'
    | 'status'
    | 'assignment'
    | 'system'
    | 'security';
  action: string;
  title: string;
  description?: string;
  actor: {
    id: string;
    name: string;
    email?: string;
    avatar?: string;
  };
  target?: {
    id: string;
    type: string;
    name: string;
    url?: string;
  };
  metadata?: Record<string, unknown>;
  timestamp: Date | string;
  icon?: React.ReactNode;
  color?: string;
  tags?: string[];
}

export interface ActivityFeedProps extends BoxProps {
  /** Activities to display */
  activities: Activity[];
  /** Whether to show loading state */
  loading?: boolean;
  /** Loading more activities */
  loadingMore?: boolean;
  /** Whether there are more activities to load */
  hasMore?: boolean;
  /** Callback to load more activities */
  onLoadMore?: () => void;
  /** Whether to group activities by date */
  groupByDate?: boolean;
  /** Whether to show actor avatars */
  showAvatars?: boolean;
  /** Maximum description length before truncation */
  maxDescriptionLength?: number;
  /** Custom empty state */
  emptyState?: React.ReactNode;
  /** Activity item click handler */
  onActivityClick?: (activity: Activity) => void;
  /** Locale for date formatting */
  locale?: string;
}

/**
 * ActivityFeed component - displays a chronological feed of activities
 * Supports infinite scroll, date grouping, and various activity types
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  groupByDate = true,
  showAvatars = true,
  maxDescriptionLength = 200,
  emptyState,
  onActivityClick,
  locale = 'en-US',
  ...boxProps
}) => {
  // Get icon for activity type
  const getActivityIcon = (activity: Activity) => {
    if (activity.icon) return activity.icon;

    switch (activity.type) {
      case 'create':
        return <FileTextIcon />;
      case 'update':
        return <UpdateIcon />;
      case 'delete':
        return <CrossCircledIcon />;
      case 'comment':
        return <ChatBubbleIcon />;
      case 'status':
        return <InfoCircledIcon />;
      case 'assignment':
        return <PersonIcon />;
      case 'system':
        return <GearIcon />;
      case 'security':
        return <LockClosedIcon />;
      default:
        return <InfoCircledIcon />;
    }
  };

  // Get color for activity type
  const getActivityColor = (activity: Activity) => {
    if (activity.color) return activity.color;

    switch (activity.type) {
      case 'create':
        return 'green.500';
      case 'update':
        return 'blue.500';
      case 'delete':
        return 'red.500';
      case 'comment':
        return 'purple.500';
      case 'status':
        return 'orange.500';
      case 'assignment':
        return 'teal.500';
      case 'system':
        return 'gray.500';
      case 'security':
        return 'yellow.500';
      default:
        return 'gray.500';
    }
  };

  // Group activities by date
  const groupedActivities = React.useMemo(() => {
    if (!groupByDate) return { ungrouped: activities };

    const groups: Record<string, Activity[]> = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    activities.forEach((activity) => {
      const date = new Date(activity.timestamp);
      let key: string;

      if (date.toDateString() === today.toDateString()) {
        key = 'Bugün';
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = 'Dün';
      } else {
        key = date.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(activity);
    });

    return groups;
  }, [activities, groupByDate, locale]);

  // Truncate description
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Render single activity
  const renderActivity = (activity: Activity) => {
    const timeAgo = formatDistanceToNow(new Date(activity.timestamp), { locale });

    return (
      <Box
        key={activity.id}
        display="flex"
        gap={3}
        padding={3}
        backgroundColor="bg.surface"
        borderRadius="lg"
        border="1px solid"
        borderColor="border.muted"
        transition="all 0.2s"
        cursor={onActivityClick ? 'pointer' : 'default'}
        _hover={
          onActivityClick
            ? {
                borderColor: 'border.default',
                backgroundColor: 'bg.muted',
                transform: 'translateY(-1px)',
                boxShadow: 'sm',
              }
            : undefined
        }
        onClick={() => onActivityClick?.(activity)}
      >
        {/* Avatar */}
        {showAvatars && (
          <Avatar
            src={activity.actor.avatar}
            name={activity.actor.name}
            size="sm"
            backgroundColor={getActivityColor(activity)}
          >
            {!activity.actor.avatar && getActivityIcon(activity)}
          </Avatar>
        )}

        {/* Icon only (when avatars are hidden) */}
        {!showAvatars && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={10}
            height={10}
            borderRadius="full"
            backgroundColor={getActivityColor(activity)}
            color="white"
            flexShrink={0}
          >
            {getActivityIcon(activity)}
          </Box>
        )}

        {/* Content */}
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="2">
            <Box flex={1} minWidth={0}>
              {/* Title with actor */}
              <Text fontSize="sm" color="fg.default">
                <Text as="span" fontWeight="semibold">
                  {activity.actor.name}
                </Text>{' '}
                <Text as="span" color="fg.muted">
                  {activity.action}
                </Text>
                {activity.target && (
                  <>
                    {' '}
                    <Text
                      as="span"
                      fontWeight="medium"
                      color="fg.default"
                      textDecoration={activity.target.url ? 'underline' : 'none'}
                    >
                      {activity.target.name}
                    </Text>
                  </>
                )}
              </Text>

              {/* Description */}
              {activity.description && (
                <Text fontSize="sm" color="fg.muted" marginTop={1}>
                  {truncateText(activity.description, maxDescriptionLength)}
                </Text>
              )}

              {/* Tags */}
              {activity.tags && activity.tags.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap" marginTop={2}>
                  {activity.tags.map((tag, index) => (
                    <Badge key={index} size="sm">
                      {tag}
                    </Badge>
                  ))}
                </Box>
              )}
            </Box>

            {/* Time */}
            <Text fontSize="xs" color="fg.muted" flexShrink={0}>
              {timeAgo}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading && activities.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" padding={8} {...boxProps}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!loading && activities.length === 0) {
    return (
      <Box {...boxProps}>
        {emptyState || (
          <EmptyState
            title="Henüz aktivite yok"
            description="Sistem üzerinde gerçekleşen aktiviteler burada görüntülenecek."
            icon={<InfoCircledIcon />}
          />
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="1rem" {...boxProps}>
      {/* Grouped activities */}
      {Object.entries(groupedActivities).map(([date, dateActivities]) => (
        <Box key={date}>
          {groupByDate && date !== 'ungrouped' && (
            <Heading size="sm" marginBottom={3} color="fg.muted">
              {date}
            </Heading>
          )}
          <Box display="flex" flexDirection="column" gap="0.5rem">
            {dateActivities.map(renderActivity)}
          </Box>
        </Box>
      ))}

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <Box display="flex" justifyContent="center" marginTop={4}>
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={loadingMore}
            leftIcon={loadingMore ? <Spinner size="sm" /> : undefined}
          >
            {loadingMore ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ActivityFeed;
